import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { Snackbar, Alert, Slide, IconButton, useTheme, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

const SnackbarContext = createContext();

// Custom hook to use the snackbar in any component
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

// Slide transition for modern feel
const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

export const SnackbarProvider = ({ children }) => {
  const theme = useTheme();
  const [snackPack, setSnackPack] = useState([]);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState(undefined);
  const queueRef = useRef([]);

  // Process queue
  React.useEffect(() => {
    if (snackPack.length && !messageInfo) {
      // Set a new snack when we don't have an active one
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      // Close an active snack when a new one is added
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  // Enhanced showSnackbar function with queue support
  const showSnackbar = useCallback((msg, sev = 'info', options = {}) => {
    const {
      duration = 4000,
      preventDuplicate = true,
    } = options;

    // Prevent duplicate messages if enabled
    if (preventDuplicate) {
      const isDuplicate = queueRef.current.some(
        (item) => item.message === msg && item.severity === sev
      );
      if (isDuplicate) return;
    }

    const newSnack = {
      message: msg,
      severity: sev,
      duration,
      key: new Date().getTime(),
    };

    queueRef.current.push(newSnack);
    setSnackPack((prev) => [...prev, newSnack]);
  }, []);

  // Convenience methods for different severities
  const showSuccess = useCallback((msg, options) => {
    showSnackbar(msg, 'success', options);
  }, [showSnackbar]);

  const showError = useCallback((msg, options) => {
    showSnackbar(msg, 'error', { duration: 5000, ...options });
  }, [showSnackbar]);

  const showWarning = useCallback((msg, options) => {
    showSnackbar(msg, 'warning', options);
  }, [showSnackbar]);

  const showInfo = useCallback((msg, options) => {
    showSnackbar(msg, 'info', options);
  }, [showSnackbar]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setMessageInfo(undefined);
    // Remove from queue reference
    queueRef.current = queueRef.current.slice(1);
  };

  // Custom icons for each severity
  const getIcon = (severity) => {
    const iconProps = { sx: { fontSize: 22 } };
    switch (severity) {
      case 'success':
        return <CheckCircleIcon {...iconProps} />;
      case 'error':
        return <ErrorIcon {...iconProps} />;
      case 'warning':
        return <WarningIcon {...iconProps} />;
      case 'info':
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  // Custom colors for better visual hierarchy
  const getSeverityStyles = (severity) => {
    const styles = {
      success: {
        backgroundColor: theme.palette.mode === 'light' 
          ? '#10b981' 
          : '#059669',
        color: '#ffffff',
      },
      error: {
        backgroundColor: theme.palette.mode === 'light'
          ? '#ef4444'
          : '#dc2626',
        color: '#ffffff',
      },
      warning: {
        backgroundColor: theme.palette.mode === 'light'
          ? '#f59e0b'
          : '#d97706',
        color: '#ffffff',
      },
      info: {
        backgroundColor: theme.palette.mode === 'light'
          ? '#3b82f6'
          : '#2563eb',
        color: '#ffffff',
      },
    };
    return styles[severity] || styles.info;
  };

  return (
    <SnackbarContext.Provider 
      value={{ 
        showSnackbar, 
        showSuccess, 
        showError, 
        showWarning, 
        showInfo 
      }}
    >
      {children}
      
      {/* Enhanced Global Snackbar with Modern Design */}
      <Snackbar
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={messageInfo?.duration}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            padding: 0,
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={messageInfo?.severity}
          icon={getIcon(messageInfo?.severity)}
          variant="filled"
          sx={{
            minWidth: 300,
            maxWidth: 500,
            ...getSeverityStyles(messageInfo?.severity),
            boxShadow: theme.palette.mode === 'light'
              ? '0 8px 32px rgba(0, 0, 0, 0.12)'
              : '0 8px 32px rgba(0, 0, 0, 0.4)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            fontWeight: 500,
            fontSize: '0.9rem',
            alignItems: 'center',
            padding: '12px 16px',
            transition: 'all 0.3s ease',
            border: `1px solid ${alpha('#ffffff', 0.1)}`,
            '& .MuiAlert-icon': {
              marginRight: '12px',
              padding: 0,
              opacity: 1,
            },
            '& .MuiAlert-message': {
              padding: '4px 0',
              flex: 1,
            },
            '& .MuiAlert-action': {
              marginRight: '-4px',
              marginLeft: '8px',
              padding: 0,
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.palette.mode === 'light'
                ? '0 12px 40px rgba(0, 0, 0, 0.15)'
                : '0 12px 40px rgba(0, 0, 0, 0.5)',
            },
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              sx={{
                padding: '4px',
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.15),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {messageInfo?.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

// Export convenience wrapper for direct import
export default SnackbarProvider;